import {getCookie, setCookie} from '@github-ui/cookies'

const COOKIE_EXPIRY_MS = 30 * 24 * 60 * 60 * 1000

export interface CookieBackedSet {
  /** Whether `id` is currently in the persisted set, parsed fresh from the cookie. */
  has(id: string): boolean
  /**
   * Adds or removes `id` from the persisted set, writing the change back to the
   * cookie. No-op when `id` is already in the desired state.
   */
  set(id: string, present: boolean): void
}

function read(cookieName: string): Set<string> {
  const raw = getCookie(cookieName)?.value ?? ''
  return new Set(
    raw
      .split(',')
      .map(s => s.trim())
      .filter(Boolean),
  )
}

/**
 * Creates a helper that persists a `Set<string>` in a comma-separated browser
 * cookie. Shared by the dashboard-surface section-state hooks, which each
 * remember a per-section flag (collapsed, previously-empty) across reloads.
 *
 * @param cookieName Name of the backing cookie.
 * @param onChange Optional callback invoked with the full set (as an array)
 * after a mutation actually changes the cookie. Used to mirror the write to a
 * durable backend as a fire-and-forget backup.
 */
export function createCookieBackedSet(cookieName: string, onChange?: (ids: string[]) => void): CookieBackedSet {
  return {
    has(id) {
      return read(cookieName).has(id)
    },
    set(id, present) {
      const current = read(cookieName)
      if (present === current.has(id)) return

      if (present) {
        current.add(id)
      } else {
        current.delete(id)
      }

      const ids = [...current]
      const expireTime = new Date(Date.now() + COOKIE_EXPIRY_MS).toUTCString()
      setCookie(cookieName, ids.join(','), expireTime)
      onChange?.(ids)
    },
  }
}
